/* global window, Intl */
import { candidateZoneLabel } from './zoneLabel';

// The bearer token stays opaque because mailers may append their own parameters.
const PROPOSAL_ID_PARAM = 'proposalId';
const PROPOSE_TOKEN_PARAM = 'token';
const LINK_TOKEN_SHAPE = /^[0-9a-f]{32}\.[A-Za-z0-9_-]{43}$/;

const PROPOSE_ROUTE_PATH = '/propose';
const PROPOSE_ROUTE_SHAPE = new RegExp(`^${PROPOSE_ROUTE_PATH}(/|$)`, 'i');

function readParam(name, search, pathname) {
  if (!PROPOSE_ROUTE_SHAPE.test(pathname)) return null;
  const value = new URLSearchParams(search).get(name);
  return value ? value.trim() : null;
}

function getProposeToken(search = window.location.search, pathname = window.location.pathname) {
  const value = readParam(PROPOSE_TOKEN_PARAM, search, pathname);
  return value && LINK_TOKEN_SHAPE.test(value) ? value : null;
}

function getProposalId(search = window.location.search, pathname = window.location.pathname) {
  return readParam(PROPOSAL_ID_PARAM, search, pathname) || null;
}

// Missing zone uses the browser; invalid zone uses visibly labelled UTC.
const UTC = 'UTC';

function browserTimeZone() {
  try {
    const resolved = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!resolved) return UTC;
    Intl.DateTimeFormat('en-US', { timeZone: resolved });
    return resolved;
  } catch (error) {
    return UTC;
  }
}

function resolveTimeZone(candidateTimeZoneId) {
  if (!candidateTimeZoneId) return browserTimeZone();
  try {
    Intl.DateTimeFormat('en-US', { timeZone: candidateTimeZoneId });
    return candidateTimeZoneId;
  } catch (error) {
    return UTC;
  }
}

const NARROW_SPACES = /[\u202f\u00a0]/g;

function formatterFor(timeZone, options) {
  const format = new Intl.DateTimeFormat(undefined, { timeZone, ...options });
  return instant => format.format(instant).replace(NARROW_SPACES, ' ');
}

function toInstant(value) {
  if (typeof value !== 'string' || !value) return null;
  const iso = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})(?::\d{2}(?:\.\d+)?)?(Z|z|[+-]\d{2}(?::?\d{2})?)?$/;
  const rfc = /^[A-Za-z]{3}, \d{2} [A-Za-z]{3} \d{4} \d{2}:\d{2}(?::\d{2})? GMT$/;
  const match = value.match(iso);
  if (!match && !rfc.test(value)) return null;
  let normalized = value;
  if (match) {
    normalized = value.replace(' ', 'T').replace(/z$/, 'Z');
    if (!match[3]) normalized += 'Z';
    else if (/^[+-]\d{2}$/.test(match[3])) normalized = `${normalized.slice(0, -3)}${match[3]}:00`;
    else if (/^[+-]\d{4}$/.test(match[3])) normalized = `${normalized.slice(0, -5)}${match[3].slice(0, 3)}:${match[3].slice(3)}`;
  }
  const instant = new Date(normalized);
  return Number.isNaN(instant.getTime()) ? null : instant;
}

const DAY_FORMAT = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
const TIME_FORMAT = { hour: 'numeric', minute: '2-digit' };

function toProposalView(payload) {
  const resolved = payload || {};
  const timeZone = resolveTimeZone(resolved.candidateTimeZoneId);
  const formatDay = formatterFor(timeZone, DAY_FORMAT);
  const formatTime = formatterFor(timeZone, TIME_FORMAT);
  const expiresAt = toInstant(resolved.expiresAtUtc);

  const slots = (Array.isArray(resolved.slots) ? resolved.slots : [])
    .map(slot => ({ slot: slot || {}, start: toInstant((slot || {}).startUtc) }))
    .filter(({ start }) => start)
    .map(({ slot, start }) => {
      const end = toInstant(slot.endUtc);
      const label = `${formatDay(start)}, ${formatTime(start)}`;
      return {
        startUtc: slot.startUtc,
        label: end ? `${label} - ${formatTime(end)}` : label,
        zoneLabel: candidateZoneLabel(timeZone, start),
      };
    });

  return {
    slots,
    candidateName: resolved.candidateName || '',
    recruiterName: resolved.recruiterName || '',
    timeZone,
    timeZoneLabel: candidateZoneLabel(timeZone, slots.length ? toInstant(slots[0].startUtc) : new Date()),
    expiresAtLabel: expiresAt ? `${formatDay(expiresAt)}, ${formatTime(expiresAt)}` : null,
  };
}

function toAcceptedTimeLabel(view, acceptedStartUtc) {
  const accepted = toInstant(acceptedStartUtc);
  if (!accepted) return null;

  const offered = view.slots.find(slot => {
    const start = toInstant(slot.startUtc);
    return start !== null && start.getTime() === accepted.getTime();
  });
  if (offered) return offered.label;

  const formatDay = formatterFor(view.timeZone, DAY_FORMAT);
  const formatTime = formatterFor(view.timeZone, TIME_FORMAT);
  return `${formatDay(accepted)}, ${formatTime(accepted)}`;
}

// Response text stays local because failed requests carry the bearer token.
function statusOf(error) {
  return error && error.response ? error.response.status : null;
}

function describeFailure(status) {
  // The link's text message is the candidate's known return channel.
  if (status === 410) {
    return {
      title: 'Those interview times are no longer available',
      description: 'Reply to the text message that brought you here and your recruiter will send new times.',
      canRetry: false,
    };
  }

  if (status === 503) {
    return {
      title: 'Choosing an interview time is temporarily unavailable',
      description: 'Please try again in a few minutes.',
      canRetry: true,
    };
  }

  if (!status || status >= 500) {
    return {
      title: 'The interview times could not be reached',
      description: 'Please try again.',
      canRetry: true,
    };
  }

  return {
    title: 'That link is not valid',
    description: 'Reply to the text message that brought you here and your recruiter will send a new link.',
    canRetry: false,
  };
}

function describeResolveFailure(error) {
  return describeFailure(statusOf(error));
}

// Accept's 502 keeps the link usable, unlike a withdrawn offer.
function describeAcceptFailure(error) {
  const status = statusOf(error);

  if (status === 502) {
    return {
      title: 'That time could not be confirmed',
      description: 'Your interview times are still available. Please try again.',
      canRetry: true,
    };
  }

  return describeFailure(status);
}

export {
  PROPOSE_ROUTE_PATH,
  LINK_TOKEN_SHAPE,
  DAY_FORMAT,
  TIME_FORMAT,
  statusOf,
  toInstant,
  formatterFor,
  resolveTimeZone,
  PROPOSAL_ID_PARAM,
  PROPOSE_TOKEN_PARAM,
  getProposalId,
  getProposeToken,
  toProposalView,
  toAcceptedTimeLabel,
  describeResolveFailure,
  describeAcceptFailure,
};
